import { ComponentProps, ReactNode } from 'react';
import Switch from 'antd/es/switch';
import clsx from 'clsx';

import { OpInfoTooltip } from '../OpInfoTooltip/OpInfoTooltip';

import './OpSwitch.scss';

interface OpSwitchProps extends ComponentProps<typeof Switch> {
  testId?: string;
  label?: string;
  tooltip?: ReactNode;
  gtm?: string;
}

/**
 * OpSwitch is a wrapper around Ant Design's Switch component.
 * It provides a toggle switch for binary on/off states, with optional labels and tooltips.
 * 
 * Use this component for enabling/disabling features, toggling settings, or any binary choice.
 * Supports accessibility features with aria-labelledby when a label is provided.
 * 
 * @example
 * <OpSwitch checked={enabled} onChange={handleToggle} label="Enable feature" />
 * <OpSwitch checked={enabled} onChange={handleToggle} label="Enable" tooltip="Turn this on to enable the feature" />
 * 
 */
export const OpSwitch = ({
  className,
  testId = 'op-switch',
  gtm,
  label,
  tooltip,
  ...switchProps
}: OpSwitchProps) => {
  // When a label is passed we generate an id for the switch to be used in the aria-labelledby attribute
  const id = label ? label.replace(/\s+/g, '-').toLowerCase() : undefined;

  return (
    <div className="op-switch-container">
      <Switch
        className={clsx('op-switch', className)}
        data-testid={testId}
        data-gtm={gtm}
        {...(label && { 'aria-labelledby': id })} // For accessibility and tests
        {...switchProps}
      />
      {label && (
        <span id={id} className="op-switch-container__label">
          {label}
        </span>
      )}
      {tooltip && (
        <OpInfoTooltip
          className="op-switch-container__tooltip"
          title={tooltip}
        />
      )}
    </div>
  );
};
